/**
 * Domain-aware copy for empty-states and chat starter suggestions.
 *
 * Keep these lists short and concrete. Each example must be a plausible chat
 * prompt the user could send to the configured AI model in this domain.
 */

import type { WorkspaceDomain } from "@/lib/api";

export interface DomainHints {
  entityHint: string;
  signalHint: string;
  /** Example to seed the Flow canvas — phrase as "X controls Y, …" relationships. */
  flowExample: string;
  /** Example to seed the Geographic canvas — phrase as "Add coordinates for …". */
  geographicExample: string;
  /** Example to seed the Scenario Tree canvas — phrase as a simulate request. */
  scenarioExample: string;
  /** Plain noun for the "top risk entities" type column on HQ. */
  riskNoun: string;
  chatStarters: string[];
}

const HINTS: Record<WorkspaceDomain, DomainHints> = {
  geopolitical: {
    entityHint: '"Add an armed group called ELN with strength 2400 in Catatumbo"',
    signalHint:
      '"On 2026-05-12 there was an armed clash in Catatumbo with 3 fatalities, reported by El Tiempo"',
    flowExample:
      '"ELN controls Catatumbo, EMC controls Cauca, Clan del Golfo controls Urabá."',
    geographicExample: '"Add coordinates for Catatumbo, Cauca, and Urabá."',
    scenarioExample:
      '"Simulate what happens if the ELN-government ceasefire collapses in the next 90 days. Build a scenario tree with branches and probabilities."',
    riskNoun: "entities",
    chatStarters: [
      "Add an armed group called ELN with strength 2400 in Catatumbo",
      "Show me all armed groups",
      "What incidents happened in Cauca this month?",
    ],
  },
  financial: {
    entityHint:
      '"Add a counterparty called Acme Capital with BBB rating and 250M USD exposure"',
    signalHint:
      '"On 2026-05-12 Moody\'s downgraded Acme Capital from BBB+ to BBB, citing margin compression"',
    flowExample:
      '"Acme Capital is a counterparty to GlobalBank, GlobalBank is exposed to the LATAM market, ACME Bonds trade on NYSE."',
    geographicExample:
      '"Add coordinates for the headquarters of Acme Capital (New York), GlobalBank (London), and Banco Andino (Bogotá)."',
    scenarioExample:
      '"If our largest counterparty\'s credit rating drops two notches over the next 90 days, build a scenario tree of downstream exposures."',
    riskNoun: "counterparties",
    chatStarters: [
      "Add a counterparty called Acme Capital with BBB rating and 250M USD exposure",
      "Show me my largest counterparty exposures",
      "Which counterparties are concentrated in the EU?",
    ],
  },
  supply_chain: {
    entityHint:
      '"Add a Tier-1 supplier called Shenzhen Components in Guangdong with 30-day lead time"',
    signalHint:
      '"On 2026-05-12 Shenzhen Components reported a 14-day delay on part SKU-A104 due to port congestion"',
    flowExample:
      '"Shenzhen Components supplies our Mexico factory, the Mexico factory produces part SKU-A104, SKU-A104 ships via the Long Beach lane."',
    geographicExample:
      '"Add coordinates for the Shenzhen Components factory (Guangdong), our Monterrey assembly plant, and the Port of Long Beach."',
    scenarioExample:
      '"If our Tier-1 supplier in Shenzhen halts production for 30 days, build a scenario tree of fallback options and customer impact."',
    riskNoun: "suppliers",
    chatStarters: [
      "Add a Tier-1 supplier called Shenzhen Components in Guangdong",
      "Which parts have no alternative supplier?",
      "Show me lanes routed through the Suez Canal",
    ],
  },
  threat: {
    entityHint:
      '"Add a threat actor called APT-Crimson active since 2021, attributed to FSB"',
    signalHint:
      '"On 2026-05-12 we observed APT-Crimson C2 traffic to 198.51.100.42, flagged by EDR"',
    flowExample:
      '"APT-Crimson targets the energy sector, the energy sector relies on SCADA-7, SCADA-7 is exposed via the OT-DMZ."',
    geographicExample:
      '"Add coordinates for the APT-Crimson C2 hosts: Frankfurt, Amsterdam, Singapore."',
    scenarioExample:
      '"If APT-Crimson pivots to a new C2 infrastructure over the next 30 days, build a scenario tree of likely targets and indicators."',
    riskNoun: "actors",
    chatStarters: [
      "Add a threat actor called APT-Crimson active since 2021",
      "Which IOCs are linked to APT-Crimson?",
      "Show me all phishing campaigns this quarter",
    ],
  },
  healthcare: {
    entityHint:
      '"Add a provider organization called Hospital San Rafael (level III, 420 beds) in Bogotá"',
    signalHint:
      '"On 2026-05-12 INVIMA flagged an adverse event for Drug XR-120 in cardiology cohorts, severity moderate"',
    flowExample:
      '"Dr. Lopez treats the cardiology cohort, the cohort is diagnosed with hypertension, the hypertension protocol prescribes Drug XR-120."',
    geographicExample:
      '"Add coordinates for Hospital San Rafael (Bogotá), Clínica Andes (Medellín), and Lab Caribe (Barranquilla)."',
    scenarioExample:
      '"If the outbreak of Pathogen-α reaches 200 cases in our coverage region within 60 days, build a scenario tree of capacity strain, supply needs, and policy responses."',
    riskNoun: "providers",
    chatStarters: [
      "Add a provider organization called Hospital San Rafael with 420 beds",
      "Show me adverse events linked to Drug XR-120",
      "Which devices have an active recall this quarter?",
    ],
  },
  compliance: {
    entityHint:
      '"Add a regulator called BaFin with jurisdiction Germany covering banking"',
    signalHint:
      '"On 2026-05-12 BaFin opened an investigation into Acme Bank for AML controls"',
    flowExample:
      '"BaFin regulates Acme Bank, Acme Bank operates in Germany, Germany falls under the new EU sanctions regime."',
    geographicExample:
      '"Add coordinates for BaFin (Bonn), Acme Bank HQ (Frankfurt), and our subsidiary in Vienna."',
    scenarioExample:
      '"If the new EU sanctions regime takes effect Q3, build a scenario tree of which products and counterparties fall in scope."',
    riskNoun: "entities",
    chatStarters: [
      "Add a regulator called BaFin with jurisdiction Germany",
      "Which subsidiaries fall under the new EU sanctions regime?",
      "Show me all open regulatory actions",
    ],
  },
  custom: {
    entityHint: '"Add a node called X of type Y with attribute Z"',
    signalHint: '"On 2026-05-12 an event happened involving entity X"',
    flowExample: '"Entity X is related to entity Y, entity Y depends on entity Z."',
    geographicExample: '"Add coordinates for the entities in your workspace."',
    scenarioExample:
      '"Pick a future event you want to stress-test and ask the model to build a scenario tree with branches and probabilities."',
    riskNoun: "entities",
    chatStarters: [
      "Add an entity to this workspace",
      "Show me all entities of type X",
      "Summarize what's in this workspace",
    ],
  },
};

export function getDomainHints(domain: WorkspaceDomain | undefined): DomainHints {
  return HINTS[domain ?? "custom"] ?? HINTS.custom;
}
